import { type QueryInteropFetchPolicy } from './mapFetchPolicyForQuery.ts';

/**
 * A fetch policy Apollo v2 actually understands. `'no-store'` is interop-only, so it must never
 * reach Apollo — `WatchQueryFetchPolicy` does not include it, and an unknown value falls through to
 * Apollo's default cache behaviour rather than erroring.
 */
export type ApolloCompatibleFetchPolicy = Exclude<QueryInteropFetchPolicy, 'no-store'>;

/**
 * Translates an interop fetch policy for the Apollo branch.
 *
 * Only `'no-store'` is rewritten, to `'no-cache'`. This is not a convenience: Apollo's `'no-cache'`
 * already means "fetch from the network and do not write to the cache", which is exactly what
 * `'no-store'` asks for. So the two branches genuinely agree on this value — it is the one policy
 * where interop parity is real rather than approximated.
 *
 * Without this mapping, `'no-store'` would silently degrade to Apollo's default `'cache-first'`,
 * which would *reintroduce* the collision `'no-store'` exists to prevent. That matters concretely:
 * `PagePanelContentQuery` runs on the Apollo path today via `useLazyQueryPromiseInterop`.
 */
export function mapFetchPolicyForApollo(
	fetchPolicy: QueryInteropFetchPolicy | undefined,
): ApolloCompatibleFetchPolicy | undefined {
	return fetchPolicy === 'no-store' ? 'no-cache' : fetchPolicy;
}
